import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Plus, Trash2, Bell, Utensils, Mountain, CheckCircle2, BellOff, Inbox, ArrowRight, Check } from "lucide-react";

const AnnouncementsTab = ({ announcements, headers, onRefresh }) => {
  const [announcementForm, setAnnouncementForm] = useState({
    title: "",
    content: "",
    type: "general",
    send_push: true,
  });
  const [youAsked, setYouAsked] = useState([]);
  const [youAskedForm, setYouAskedForm] = useState({ asked: "", did: "" });
  const [feedback, setFeedback] = useState([]);

  const fetchYouAsked = async () => {
    try {
      const res = await axios.get(`${API}/community/you-asked`, { headers });
      setYouAsked(res.data || []);
    } catch (error) {
      // silent
    }
  };

  const fetchFeedback = async () => {
    try {
      const res = await axios.get(`${API}/admin/feedback-inbox`, { headers });
      setFeedback((res.data || []).filter(f => !f.resolved));
    } catch (error) {
      // silent
    }
  };

  useEffect(() => {
    fetchYouAsked();
    fetchFeedback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addYouAsked = async () => {
    if (!youAskedForm.asked || !youAskedForm.did) {
      toast.error("Fill in both fields");
      return;
    }
    try {
      await axios.post(`${API}/admin/you-asked`, youAskedForm, { headers });
      toast.success("Added to 'You asked, we did'");
      setYouAskedForm({ asked: "", did: "" });
      fetchYouAsked();
    } catch (error) {
      toast.error("Failed to add item");
    }
  };

  const deleteYouAsked = async (id) => {
    try {
      await axios.delete(`${API}/admin/you-asked/${id}`, { headers });
      toast.success("Item removed");
      fetchYouAsked();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const createAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) {
      toast.error("Please fill in title and content");
      return;
    }

    try {
      await axios.post(`${API}/announcements`, announcementForm, { headers });
      toast.success(announcementForm.send_push ? "Posted + push sent" : "Posted quietly (no push)");
      setAnnouncementForm({ title: "", content: "", type: "general", send_push: true });
      onRefresh();
    } catch (error) {
      toast.error("Failed to create announcement");
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/announcements/${id}`, { headers });
      toast.success("Announcement deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  const wipeAllUpdates = async () => {
    const confirmed = window.confirm(
      "Wipe every announcement and notification for all guests? This can't be undone — guests will see a clean Updates feed."
    );
    if (!confirmed) return;
    try {
      const res = await axios.post(`${API}/admin/announcements/wipe-all`, {}, { headers });
      const d = res.data?.deleted || {};
      toast.success(
        `Wiped ${d.announcements || 0} announcements + ${d.notifications || 0} notifications`
      );
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to wipe updates");
    }
  };

  return (
          <div className="space-y-6">
            {/* Wipe-all helper — start fresh across every guest's Updates feed */}
            <div className="bg-[#FBE9E4] border border-[#E07A5F]/30 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-[#3D4035]">Start fresh</p>
                <p className="text-xs text-[#3D4035]/65">Wipes every announcement, push notification and read/dismiss record across all guests. Guests will see an empty Updates feed on next refresh.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={wipeAllUpdates}
                className="border-red-300 text-red-600 hover:bg-red-50 self-start sm:self-auto"
                data-testid="wipe-all-updates-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Wipe all updates
              </Button>
            </div>

            {/* Create Announcement */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-stone-100">
              <h3 className="font-medium text-[#3D4035] mb-4 text-sm sm:text-base">Post Announcement</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="sm:col-span-2 space-y-2">
                    <Label className="text-xs sm:text-sm">Title</Label>
                    <Input
                      value={announcementForm.title}
                      onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                      placeholder="e.g., Community Dinner Tonight"
                      className="rounded-xl text-sm"
                      data-testid="announcement-title-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs sm:text-sm">Type</Label>
                    <select
                      value={announcementForm.type}
                      onChange={(e) => setAnnouncementForm({ ...announcementForm, type: e.target.value })}
                      className="w-full h-10 rounded-xl border border-stone-200 px-3 text-sm"
                      data-testid="announcement-type-select"
                    >
                      <option value="general">General</option>
                      <option value="dinner">Dinner</option>
                      <option value="activity">Activity</option>
                      <option value="reminder">Reminder</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs sm:text-sm">Content</Label>
                  <Textarea
                    value={announcementForm.content}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, content: e.target.value })}
                    placeholder="What's happening at Savi?"
                    className="rounded-xl min-h-[80px] text-sm"
                    data-testid="announcement-content-input"
                  />
                </div>
                <div
                  className="flex items-center justify-between gap-3 bg-[#F4ECE2] rounded-xl px-3 py-2.5"
                  data-testid="announcement-push-toggle-wrap"
                >
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-[#3D4035]">
                    {announcementForm.send_push ? (
                      <Bell className="w-4 h-4 text-[#E07A5F]" />
                    ) : (
                      <BellOff className="w-4 h-4 text-[#3D4035]/40" />
                    )}
                    <span>
                      {announcementForm.send_push
                        ? "Ping everyone's phone with a push notification"
                        : "Post quietly — no push notification"}
                    </span>
                  </div>
                  <Switch
                    checked={!!announcementForm.send_push}
                    onCheckedChange={(v) =>
                      setAnnouncementForm({ ...announcementForm, send_push: v })
                    }
                    data-testid="announcement-push-toggle"
                  />
                </div>
                <Button
                  onClick={createAnnouncement}
                  className="bg-[#E07A5F] hover:bg-[#D06A4F] rounded-full text-sm w-full sm:w-auto"
                  data-testid="post-announcement-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Post Announcement
                </Button>
              </div>
            </div>

            {/* Existing Announcements */}
            <div className="space-y-4">
              <h3 className="font-medium text-[#3D4035] text-sm sm:text-base">Recent Announcements</h3>
              {announcements.length > 0 ? (
                announcements.map((ann) => (
                  <div key={ann.id} className="bg-white rounded-xl p-4 border border-stone-100 flex items-start justify-between gap-4">
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${
                        ann.type === "dinner" ? "bg-[#E07A5F]/10 text-[#E07A5F]" :
                        ann.type === "activity" ? "bg-[#81B29A]/10 text-[#81B29A]" :
                        "bg-stone-100 text-stone-500"
                      }`}>
                        {ann.type === "dinner" ? <Utensils className="w-4 h-4" /> :
                         ann.type === "activity" ? <Mountain className="w-4 h-4" /> :
                         <Bell className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="font-medium text-[#3D4035] text-sm">{ann.title}</p>
                        <p className="text-[#3D4035]/60 text-xs mt-1">{ann.content}</p>
                        <p className="text-[#3D4035]/40 text-xs mt-2">
                          {new Date(ann.created_at).toLocaleDateString('en-GB', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteAnnouncement(ann.id)}
                      className="text-red-400 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-announcement-${ann.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-2xl p-8 border border-stone-100 text-center">
                  <Bell className="w-12 h-12 text-[#3D4035]/20 mx-auto mb-4" />
                  <p className="text-[#3D4035]/60">No announcements yet</p>
                </div>
              )}
            </div>

            {/* Feedback inbox — submissions from the public /feedback poster */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-stone-100">
              <h3 className="font-medium text-[#3D4035] mb-1 text-sm sm:text-base flex items-center gap-2">
                <Inbox className="w-4 h-4 text-[#E07A5F]" /> Feedback Inbox
              </h3>
              <p className="text-xs text-[#3D4035]/50 mb-4">
                What guests submitted via the kitchen QR poster. Tap &ldquo;Use&rdquo; to pre-fill the &ldquo;You asked, we did&rdquo; entry below.
              </p>
              {feedback.length === 0 ? (
                <div className="text-xs text-[#3D4035]/40 italic">No new submissions.</div>
              ) : (
                <div className="space-y-2">
                  {feedback.slice(0, 10).map((f) => (
                    <div
                      key={f.id}
                      className="flex items-start justify-between gap-2 bg-stone-50 rounded-xl p-3"
                      data-testid={`feedback-item-${f.id}`}
                    >
                      <div className="min-w-0 text-sm flex-1">
                        <div className="text-[10px] uppercase tracking-wider text-[#3D4035]/40 flex items-center gap-2">
                          <span>
                            {f.category === "idea" ? "💡 Idea" :
                             f.category === "fix" ? "🔧 Needs fixing" : "💬 Feedback"}
                          </span>
                          <span>·</span>
                          <span>{f.name || "Anonymous"}</span>
                          <span>·</span>
                          <span>
                            {new Date(f.created_at).toLocaleDateString("en-GB", {
                              day: "numeric", month: "short",
                            })}
                          </span>
                        </div>
                        <p className="text-[#3D4035] mt-1 whitespace-pre-line break-words">
                          {f.message}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setYouAskedForm({ asked: f.message, did: "" });
                            toast.success("Loaded into 'You asked, we did' — fill in what you did.");
                            // scroll the form into view
                            setTimeout(() => {
                              document.querySelector('[data-testid="we-did-input"]')?.focus();
                            }, 100);
                          }}
                          className="rounded-full text-xs"
                          data-testid={`feedback-convert-${f.id}`}
                          title="Convert to 'You asked, we did'"
                        >
                          <ArrowRight className="w-3 h-3 mr-1" /> Use
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await axios.patch(`${API}/admin/feedback-inbox/${f.id}/resolved`, {}, { headers });
                              fetchFeedback();
                            } catch (e) {
                              toast.error("Couldn't dismiss");
                            }
                          }}
                          className="rounded-full text-xs text-[#3D4035]/60 hover:text-[#3D4035]"
                          data-testid={`feedback-dismiss-${f.id}`}
                          title="Dismiss"
                        >
                          <Check className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* You asked, we did */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-stone-100">
              <h3 className="font-medium text-[#3D4035] mb-1 text-sm sm:text-base flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#81B29A]" /> You asked, we did
              </h3>
              <p className="text-xs text-[#3D4035]/50 mb-4">Pinned on every guest&apos;s dashboard — show them their feedback gets acted on.</p>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs sm:text-sm">You asked</Label>
                    <Input
                      value={youAskedForm.asked}
                      onChange={(e) => setYouAskedForm({ ...youAskedForm, asked: e.target.value })}
                      placeholder="e.g., More oat milk in the kitchen"
                      className="rounded-xl text-sm"
                      data-testid="you-asked-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs sm:text-sm">We did</Label>
                    <Input
                      value={youAskedForm.did}
                      onChange={(e) => setYouAskedForm({ ...youAskedForm, did: e.target.value })}
                      placeholder="e.g., Weekly oat milk now on the shopping list"
                      className="rounded-xl text-sm"
                      data-testid="we-did-input"
                    />
                  </div>
                </div>
                <Button
                  onClick={addYouAsked}
                  className="bg-[#81B29A] hover:bg-[#6A9A82] text-white rounded-xl text-sm"
                  data-testid="add-you-asked-button"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              {youAsked.length > 0 && (
                <div className="mt-4 space-y-2">
                  {youAsked.map((item) => (
                    <div key={item.id} className="flex items-start justify-between gap-2 bg-stone-50 rounded-xl p-3" data-testid={`you-asked-item-${item.id}`}>
                      <div className="min-w-0 text-sm">
                        <p className="text-[#3D4035]/60">&ldquo;{item.asked}&rdquo;</p>
                        <p className="text-[#3D4035] flex items-start gap-1 mt-0.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#81B29A] mt-0.5 flex-shrink-0" /> {item.did}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteYouAsked(item.id)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                        data-testid={`delete-you-asked-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
  );
};

export default AnnouncementsTab;
